import { useEffect, useState, type ComponentType, type SVGProps } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import PlanverseCard from '../components/orbi/PlanverseCard';
import { DocumentDuplicateIcon, EyeIcon, HeartIcon } from '@heroicons/react/24/outline';
import {
  getPlan,
  listPlans,
  duplicatePlan,
  type PlanversePlan,
} from '../lib/planverse';

type IconComponent = ComponentType<SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 60) return `${Math.max(mins, 1)} minute${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  return `${Math.round(hrs / 24)} days ago`;
}

function Stat({ icon: StatIcon, value, label }: { icon: IconComponent; value: number; label: string }) {
  return (
    <span className="flex items-center gap-1 text-muted-foreground" aria-label={`${value} ${label}`}>
      <StatIcon className="h-4 w-4" />
      <span className="text-sm">{value}</span>
    </span>
  );
}

export default function PlanversePreview() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState<PlanversePlan | null>(null);
  const [more, setMore] = useState<PlanversePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [duplicating, setDuplicating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([getPlan(id), listPlans()]).then(([p, all]) => {
      if (cancelled) return;
      setPlan(p);
      setMore(all.filter((x) => x.id !== id).slice(0, 8));
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleDuplicate = async () => {
    if (!plan) return;
    setDuplicating(true);
    try {
      const result = await duplicatePlan(plan.id);
      if (result?.draftId) {
        navigate(`/orbi/workflow/${result.draftId}`);
      } else {
        // Workflow backend duplicate isn't wired yet — drop them into a fresh canvas.
        toast.success('Copied to your workspace', {
          description: 'Opening Workflow with a starter canvas.',
        });
        navigate('/orbi/workflow');
      }
    } catch (err) {
      toast.error('Could not duplicate this plan. Try again in a moment.');
    } finally {
      setDuplicating(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-10 font-display">
        <div className="h-6 w-40 animate-pulse rounded bg-muted" />
        <div className="aspect-[16/9] w-full animate-pulse rounded-2xl bg-muted" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-4 px-4 py-24 text-center font-display">
        <p className="text-base text-foreground">This plan doesn’t exist.</p>
        <Link to="/planverse" className="text-sm text-primary hover:opacity-80">
          Back to Planverse
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-10 px-4 py-8 font-display md:py-12">
      <section className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="truncate text-lg text-foreground md:text-xl">{plan.title}</h1>
          <div className="flex items-center gap-4">
            <Stat icon={HeartIcon} value={plan.likes} label="likes" />
            <Stat icon={DocumentDuplicateIcon} value={plan.duplicates} label="duplicates" />
            <Stat icon={EyeIcon} value={plan.views} label="views" />
            <Button
              onClick={handleDuplicate}
              disabled={duplicating}
              className="bg-primary px-5 text-primary-foreground hover:bg-primary/90"
            >
              {duplicating ? 'Duplicating…' : 'Duplicate'}
            </Button>
          </div>
        </div>

        <div className="relative aspect-[16/9] w-full overflow-hidden rounded-2xl bg-muted">
          <img
            src={plan.coverUrl}
            alt={plan.title}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border text-xs text-white"
              style={{ backgroundColor: '#f79878', borderColor: '#f47950' }}
              aria-hidden
            >
              {plan.author.initial}
            </span>
            <div className="flex flex-col">
              <p className="text-sm font-medium text-foreground">{plan.author.name}</p>
              <p className="text-xs text-muted-foreground">Published {timeAgo(plan.publishedAt)}</p>
            </div>
          </div>
          <Link
            to={`/portfolio/${plan.author.username}`}
            className="rounded-full bg-primary px-4 py-1.5 text-sm text-primary-foreground transition-opacity hover:opacity-90"
          >
            See Portfolio
          </Link>
        </div>
      </section>

      {more.length > 0 && (
        <section className="flex flex-col gap-4">
          <h2 className="text-base text-muted-foreground">More on Planverse</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {more.map((p) => (
              <PlanverseCard
                key={p.id}
                title={p.title}
                imageUrl={p.coverUrl}
                timestamp={timeAgo(p.publishedAt)}
                authorInitial={p.author.initial}
                likes={p.likes}
                duplicates={p.duplicates}
                views={p.views}
                onClick={() => navigate(`/planverse/${p.id}`)}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
